import React from 'react';
import { Button, Input, Typography, Upload } from 'antd';
const Konva = require('konva');
import { useEffect, useRef, useState } from 'react';

import { ReactComponent as UploadIcon } from 'svg/graphicEditor/upload-white.svg';
import { DEFAULT_IMAGE } from '../../../../../config';
import SelectControl from '../../controls/SelectControl/SelectControl';
import ImageGallery from '../../ImageGallery/ImageGallery';
import Portal from '../../Portal';

import usePropertiesStore from '../../../stores/usePropertiesStore';
import useProductStore from '../../../stores/useProductStore';
import useDesignStore from '../../../stores/useDesignStore';

import styles from '../../GlobalGraphicEditor.module.css';
import myStyles from './ImageData.module.css';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const buildRequestUrl = (url, bustCache) => {
  if (!bustCache || /^data:|^blob:/i.test(url)) {
    return url;
  }

  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.set('_ts', Date.now().toString());
    return parsed.toString();
  } catch (error) {
    return url;
  }
};

const createImagePromise = (url, timeoutMs) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    let timeoutId;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      img.onload = null;
      img.onerror = null;
    };

    img.onload = () => {
      cleanup();
      resolve(img);
    };

    img.onerror = (error) => {
      cleanup();
      reject(error || new Error('Image failed to load'));
    };

    if (timeoutMs) {
      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Image load timed out'));
      }, timeoutMs);
    }

    img.src = url;
  });

const loadImageWithRetry = async (
  url,
  { maxRetries = 3, timeoutMs = 10000, bustCache = true } = {},
) => {
  if (!url) {
    throw new Error('Image URL is required');
  }

  let failure;
  for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
    try {
      const requestUrl = buildRequestUrl(url, bustCache);
      // eslint-disable-next-line no-await-in-loop
      return await createImagePromise(requestUrl, timeoutMs);
    } catch (error) {
      failure = error;
      if (attempt === maxRetries) {
        throw error;
      }
      // eslint-disable-next-line no-await-in-loop
      await delay(300 * attempt);
    }
  }

  throw failure;
};

const { Text } = Typography;
export default function (props) {
  const group = usePropertiesStore((s) => s.selectedGroup);
  const onPropertyChanged = usePropertiesStore((s) => s.propertyChanged);
  const product = useProductStore((s) => s.product);
  const productFields = useProductStore((s) => s.productFields);
  const userImages = useDesignStore((s) => s.userImages);

  const background = group?.findOne('.background');

  const [image, setImage] = useState();
  const [inputPlaceholderValue, setInputPlaceholder] = useState();
  const [inputDynamicFieldValue, setInputDynamicFieldValue] = useState();
  const [imageType, setImageType] = useState();
  const [imageId, setImageId] = useState();
  const [galleryPosition, setGalleryPosition] = useState({ left: 0, top: 0 });
  const [isLibraryOpen, setLibraryOpen] = useState();
  const latestImageRequestRef = useRef(0);

  useEffect(() => {
    if (!group) return;
    const img = group.findOne('Image');
    if (!img) return;
    setImage(img);
    setImageType(img.getAttr('dynamicField') ? 'dynamic' : 'static');
    setInputDynamicFieldValue(img.getAttr('dynamicField') || '');
    setInputPlaceholder(img.getAttr('placeholder'));
    if (!img.getAttr('dynamicField')) {
      const selected = userImages.find(
        (t) => t.image === img.getAttr('url'),
      );
      if (selected && selected.id !== imageId) {
        setImageId(selected.id);
      } else if (!selected) {
        setImageId(undefined);
      }
    }
  }, [group]);


  const replaceImageOnStage = (currentImage, imgAttrs, imageObj, url) => {
    const oldImage = imgAttrs.image;
    delete imgAttrs.image;
    delete imgAttrs.width;
    delete imgAttrs.height;

    const imgNode = new Konva.Image({
      ...imgAttrs,
      url,
      image: imageObj,
      initWidth: imageObj.width,
      initHeight: imageObj.height,
    });

    background.fillPatternImage(imageObj);
    group.add(imgNode);
    if (oldImage) oldImage.remove();
    currentImage.destroy();

    setImage(imgNode);
    props.scaleImage(imgNode);
  };

  const changeDynamicField = async (newField) => {
    if (!image) {
      return;
    }

    const currentImage = image;
    currentImage.setAttr('dynamicField', newField || undefined);
    const imgAttrs = currentImage.getAttrs();
    const nextUrl = newField ? product?.[newField] : imgAttrs.placeholder;
    if (!nextUrl) {
      console.error('No image URL found for dynamic field change');
      propertyChanged();
      return;
    }
    const requestId = Date.now();
    latestImageRequestRef.current = requestId;

    try {
      const loadedImage = await loadImageWithRetry(nextUrl);
      if (latestImageRequestRef.current !== requestId) {
        return;
      }
      replaceImageOnStage(currentImage, imgAttrs, loadedImage, nextUrl);
    } catch (error) {
      console.error('Failed to load dynamic image', error);
    } finally {
      propertyChanged();
    }
  };

  const changePlaceholderImage = async (newUrl) => {
    if (!image) {
      return;
    }

    const nextUrl = newUrl || DEFAULT_IMAGE;
    const currentImage = image;
    currentImage.setAttr('placeholder', nextUrl);
    setInputPlaceholder(nextUrl);

    if (currentImage.getAttr('dynamicField')) {
      return;
    }

    const imgAttrs = currentImage.getAttrs();
    const requestId = Date.now();
    latestImageRequestRef.current = requestId;

    try {
      const imageObj = await loadImageWithRetry(nextUrl);
      if (latestImageRequestRef.current !== requestId) {
        return;
      }

      replaceImageOnStage(currentImage, imgAttrs, imageObj, nextUrl);
      imageChanged();
    } catch (error) {
      console.error('Image load failed:', error);

      if (nextUrl !== DEFAULT_IMAGE) {
        try {
          const fallback = await loadImageWithRetry(DEFAULT_IMAGE, {
            maxRetries: 1,
            bustCache: false,
          });
          if (latestImageRequestRef.current === requestId) {
            currentImage.setAttr('placeholder', DEFAULT_IMAGE);
            replaceImageOnStage(currentImage, imgAttrs, fallback, DEFAULT_IMAGE);
            setInputPlaceholder(DEFAULT_IMAGE);
            imageChanged();
          }
        } catch (fallbackError) {
          console.error('Fallback image load failed:', fallbackError);
        }
      }
    }
  };

  const onDynamicFieldChanged = (value) => {
    setInputDynamicFieldValue(value);
    changeDynamicField(value);
  };

  const customRequest = ({ onSuccess, onError, file }) => {
    props.uploadFile(file).then(
      (result) => {
        onSuccess(null, file);
        if (result) {
          changePlaceholderImage(result.image);
          setImageId(result.id);
        }
      },
      (error) => onError(error),
    );
  };

  const setLibraryImage = (id) => {
    const image = userImages.find((t) => t.id === id);
    if (image) {
      const imageUrl = image.image;
      changePlaceholderImage(imageUrl);
      setImageId(id);
      openLibrary();
    }
  };

  const uploadProps = {
    fileList: [],
    multiple: false,
  };

  const linkRef = useRef();

  const openLibrary = () => {
    if (!isLibraryOpen) {
      const el = linkRef.current;
      const left = el.offsetLeft + el.clientWidth + 175;
      let top = el.offsetTop - 114;
      if (top < 5) {
        top = 5;
      }
      setGalleryPosition({ left, top });
    }
    setLibraryOpen(!isLibraryOpen);
  };

  const deleteImage = async (id) => {
    await props.deleteImage(id);
    await changePlaceholderImage(DEFAULT_IMAGE);
  };

  const propertyChanged = () => {
    if (onPropertyChanged) {
      onPropertyChanged();
    }
  };

  const imageChanged = () => {
    if (props.onImageChanged) {
      props.onImageChanged();
    }
  };

  return (
    <div>
      {imageType === 'static' && (
        <div
          className={`${styles.selectBlock} ${myStyles.actionButtonsWrapper}`}
        >
          <div className={`${styles.selectBlock} ${myStyles.actionButtonsTop}`}>
            <div className={styles.selectBlock}>
              <Text>Url</Text>
              <Input
                type='text'
                onChange={(e) => changePlaceholderImage(e.target.value)}
                value={inputPlaceholderValue}
                className={myStyles.input}
              />
            </div>
            <div className={`${styles.selectBlock}`}>
              <div ref={linkRef}>
                <button
                  type='button'
                  onClick={openLibrary}
                  className={myStyles.openLibraryButton}
                >
                  Select from library
                </button>
              </div>
            </div>
          </div>
          <Upload
            {...uploadProps}
            customRequest={(onSuccess, onError, file) =>
              customRequest(onSuccess, onError, file)
            }
            className={myStyles.uploadHandler}
          >
            <Button className={myStyles.uploadButton} icon={<UploadIcon />}>
              Upload
            </Button>
          </Upload>
        </div>
      )}

      {imageType === 'dynamic' && product && (
        <div>
          <SelectControl
            value={inputDynamicFieldValue}
            label='Field'
            onChange={onDynamicFieldChanged}
            dataSource={[
              ...Object.keys(product).map((item) => {
                return {
                  id: item,
                  name: item,
                  elementStyle: item.startsWith('__')
                    ? { color: '#3333ff' }
                    : null,
                };
              }),
            ]}
          />
        </div>
      )}

      {isLibraryOpen && (
        <Portal>
          <ImageGallery
            setLibraryImage={setLibraryImage}
            onClose={openLibrary}
            userImages={userImages}
            deleteImage={deleteImage}
          />
        </Portal>
      )}
    </div>
  );
}
